"use client";
import { getEnv } from "@/lib/env";

let BASE_URL: string | null = null;

/**
 * Standard API response structure used across the application
 * @template T Type of the data field in the response
 * @template M Type of the metadata field in the response
 * @template P Type of the pagination field in the response
 */
interface ApiResponse<T = any, M = any, P = any> {
  data?: T;
  meta?: M;
  pagination?: P;
  statusCode?: number;
  error?: string;
  message?: string[];
  timestamp?: string;
  path?: string;
  status?: number;
}

/**
 * Retrieves and caches the base URL for API requests from environment variables
 * @returns {string} The base URL for API endpoints
 */
export async function getBaseUrl(): Promise<string> {
  if (!BASE_URL) {
    const env = await getEnv();
    BASE_URL = `${env.NEXT_PUBLIC_EXTERNAL_API_URL}`;
  }
  return BASE_URL;
}

/**
 * Makes a fetch request to the API with proper error handling and authentication
 * @template T The expected data type of the response
 * @template M The expected metadata type of the response
 * @template P The expected pagination type of the response
 * @param {string} url The endpoint URL (will be appended to base URL unless it's a full URL)
 * @param {RequestInit} options Fetch options (method, headers, body, etc.)
 * @param {boolean} isMultiPart Whether the request contains multipart form data
 * @returns {Promise<ApiResponse<T, M, P>>} The API response with data, metadata, and pagination
 * @throws Will redirect to login page on 401 unauthorized responses
 * @throws Will propagate any network or parsing errors
 */
export async function customFetch<T, M = any, P = any>(
  url: string,
  options: RequestInit = {},
  isMultiPart: boolean = false,
  responseType: 'json' | 'blob' | 'text' = 'json'
): Promise<ApiResponse<T, M, P>> {
  try {
    const baseUrl = await getBaseUrl();

    const response = await fetch(
      url.startsWith("http") ? url : `${baseUrl}${url}`,
      {
        ...options,
        credentials: "include",
        headers: {
          ...(isMultiPart ? {} : { "Content-Type": "application/json" }),
          ...options.headers,
        },
      }
    );

    // Handle non-JSON responses if requested
    if (responseType === 'blob') {
      const blob = await response.blob();
      // Minimal ApiResponse shape with blob in data
      return { data: blob as any, status: response.status } as any;
    }
    if (responseType === 'text') {
      const text = await response.text();
      return { data: text as any, status: response.status } as any;
    }

    const json = await response.json();

    return json;
  } catch (error) {
    if (error instanceof Error && error.message === "Forbidden") {
      throw new Error("Forbidden");
    }

    console.error("API request failed:", error);
    throw error;
  }
}
